import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBook, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import styles from './WikiPage.module.css';

interface ArticleSummary {
  id: number;
  slug: string;
  title: string;
  category: string;
  categorySortOrder: number;
  sortOrder: number;
}

interface ArticleFull extends ArticleSummary {
  content: string;
}

interface RaceBonus {
  raceName: string;
  health: number;
  strength: number;
  endurance: number;
  learningCapacity: number;
  discipline: number;
  initiative: number;
  avoidance: number;
  luck: number;
  weaponAxes: number;
  weaponSwords: number;
  weaponMaces: number;
  weaponStaves: number;
  weaponDaggers: number;
  weaponChain: number;
  weaponShields: number;
  weight: number;
  minimumCritChance: number;
}

interface RaceAgeBonus {
  stage: string;
  thresholdDays: number;
  health: number;
  strength: number;
  endurance: number;
  initiative: number;
  dodge: number;
  leadership: number;
  learningCapacity: number;
  luck: number;
  discipline: number;
  provocation: number;
}

interface CategoryGroup {
  name: string;
  sortOrder: number;
  articles: ArticleSummary[];
}

const RACE_SLUGS = ['race-human', 'race-dwarf', 'race-elf', 'race-orc', 'race-troll', 'race-goblin'];

// Map wiki slug → race name in RaceBonus table
const SLUG_TO_RACE: Record<string, string> = {
  'race-human': 'Human',
  'race-dwarf': 'Dwarf',
  'race-elf': 'Elf',
  'race-orc': 'Orc',
  'race-troll': 'Troll',
  'race-goblin': 'Goblin',
};

function groupByCategory(articles: ArticleSummary[]): CategoryGroup[] {
  const map = new Map<string, CategoryGroup>();
  for (const a of articles) {
    if (!map.has(a.category)) {
      map.set(a.category, { name: a.category, sortOrder: a.categorySortOrder, articles: [] });
    }
    map.get(a.category)!.articles.push(a);
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.sortOrder - b.sortOrder);
  for (const g of groups) {
    g.articles.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  return groups;
}

function formatModifier(value: number): string {
  const pct = Math.round((value - 1) * 100);
  if (pct === 0) return 'No change';
  return pct > 0 ? `+${pct}%` : `${pct}%`;
}

const WikiPage: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<CategoryGroup[]>([]);
  const [currentArticle, setCurrentArticle] = useState<ArticleFull | null>(null);
  const [raceBonus, setRaceBonus] = useState<RaceBonus | null>(null);
  const [ageStages, setAgeStages] = useState<RaceAgeBonus[]>([]);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Fetch article list on mount
  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await axios.get('/api/wiki/articles', { withCredentials: true });
        const groups = groupByCategory(res.data);
        setCategories(groups);

        // Only expand the first category ("Getting Started") by default
        if (groups.length > 0) {
          setExpandedCategories(new Set([groups[0].name]));
        }
      } catch (err) {
        console.error('Failed to fetch wiki articles', err);
        setError('Failed to load the guide. Please try again.');
      } finally {
        setLoadingList(false);
      }
    };
    fetchList();
  }, []);

  // Redirect to first article if no slug is present and list has loaded
  useEffect(() => {
    if (!slug && !loadingList && categories.length > 0) {
      const firstSlug = categories[0]?.articles[0]?.slug;
      if (firstSlug) {
        navigate(`/wiki/${firstSlug}`, { replace: true });
      }
    }
  }, [slug, loadingList, categories, navigate]);

  // Fetch full article when slug changes
  useEffect(() => {
    if (!slug) return;
    const fetchArticle = async () => {
      setLoadingArticle(true);
      setRaceBonus(null);      setAgeStages([]);      setError(null);
      try {
        const res = await axios.get(`/api/wiki/articles/${slug}`, { withCredentials: true });
        setCurrentArticle(res.data);

        // Auto-expand the category of the active article
        setExpandedCategories((prev) => new Set([...prev, res.data.category]));

        // Fetch race bonus if this is a race article
        if (RACE_SLUGS.includes(slug)) {
          const raceName = SLUG_TO_RACE[slug];
          try {
            const rbRes = await axios.get(`/api/mercenary/race-bonus?race=${raceName}`, { withCredentials: true });
            // Endpoint returns an array; grab the first item
            const bonusData = Array.isArray(rbRes.data) ? rbRes.data[0] : rbRes.data;
            setRaceBonus(bonusData ?? null);
          } catch {
            // Not critical - just won't show the bonus table
          }
          try {
            const ageRes = await axios.get(`/api/mercenary/race-age-bonus?race=${raceName}`, { withCredentials: true });
            setAgeStages(Array.isArray(ageRes.data) ? ageRes.data : []);
          } catch {
            // Not critical
          }
        }
      } catch (err) {
        console.error('Failed to fetch wiki article', err);
        setError('Article not found or could not be loaded.');
        setCurrentArticle(null);
      } finally {
        setLoadingArticle(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const toggleCategory = useCallback((name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  return (
    <div className={styles.wikiContainer}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <FontAwesomeIcon icon={faBook} className={styles.bookIcon} />
          <span>Game Guide</span>
        </div>

        <Link to="/home" className={styles.backButton}>
          <FontAwesomeIcon icon={faArrowLeft} className={styles.backIcon} />
          Back to Game
        </Link>

        <nav className={styles.sidebarNav}>
          {loadingList ? (
            <div className={styles.sidebarLoading}>Loading…</div>
          ) : (
            categories.map((group) => (
              <div key={group.name} className={styles.categoryBlock}>
                <button
                  className={styles.categoryToggle}
                  onClick={() => toggleCategory(group.name)}
                >
                  <FontAwesomeIcon
                    icon={expandedCategories.has(group.name) ? faChevronDown : faChevronRight}
                    className={styles.chevron}
                  />
                  {group.name}
                </button>
                {expandedCategories.has(group.name) && (
                  <ul className={styles.articleList}>
                    {group.articles.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/wiki/${a.slug}`}
                          className={`${styles.articleLink} ${slug === a.slug ? styles.activeLink : ''}`}
                        >
                          {a.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </nav>
      </aside>

      {/* ── Main Content ── */}
      <main className={styles.content}>
        {loadingArticle && (
          <div className={styles.loadingState}>Loading article…</div>
        )}
        {error && !loadingArticle && (
          <div className={styles.errorState}>{error}</div>
        )}
        {!loadingArticle && !error && currentArticle && (
          <>
            <article className={styles.article}>
              <ReactMarkdown>{currentArticle.content}</ReactMarkdown>
            </article>

            {/* Race Bonus Table - only shown on race articles */}
            {raceBonus && (
              <section className={styles.raceBonusSection}>
                <h2 className={styles.raceBonusTitle}>Racial Modifiers</h2>
                <p className={styles.raceBonusNote}>
                  These values are the actual live bonuses applied to this race in the current season. Positive values
                  mean stats scale up; negative values mean they scale down relative to baseline.
                </p>
                <div className={styles.bonusTables}>
                  <div className={styles.bonusTableBlock}>
                    <h3>Stats</h3>
                    <table className={styles.bonusTable}>
                      <thead>
                        <tr>
                          <th>Attribute</th>
                          <th>Modifier</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Health</td><td className={getModClass(raceBonus.health)}>{formatModifier(raceBonus.health)}</td></tr>
                        <tr><td>Strength</td><td className={getModClass(raceBonus.strength)}>{formatModifier(raceBonus.strength)}</td></tr>
                        <tr><td>Endurance</td><td className={getModClass(raceBonus.endurance)}>{formatModifier(raceBonus.endurance)}</td></tr>
                        <tr><td>Initiative</td><td className={getModClass(raceBonus.initiative)}>{formatModifier(raceBonus.initiative)}</td></tr>
                        <tr><td>Dodge</td><td className={getModClass(raceBonus.avoidance)}>{formatModifier(raceBonus.avoidance)}</td></tr>
                        <tr><td>Luck</td><td className={getModClass(raceBonus.luck)}>{formatModifier(raceBonus.luck)}</td></tr>
                        <tr><td>Learning Capacity</td><td className={getModClass(raceBonus.learningCapacity)}>{formatModifier(raceBonus.learningCapacity)}</td></tr>
                        <tr><td>Discipline</td><td className={getModClass(raceBonus.discipline)}>{formatModifier(raceBonus.discipline)}</td></tr>
                        <tr><td>Base Weight</td><td>{raceBonus.weight}</td></tr>
                        <tr><td>Min. Crit Chance</td><td>+{raceBonus.minimumCritChance}%</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.bonusTableBlock}>
                    <h3>Weapon Skills</h3>
                    <table className={styles.bonusTable}>
                      <thead>
                        <tr>
                          <th>Weapon Type</th>
                          <th>Modifier</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Axes</td><td className={getModClass(raceBonus.weaponAxes)}>{formatModifier(raceBonus.weaponAxes)}</td></tr>
                        <tr><td>Swords</td><td className={getModClass(raceBonus.weaponSwords)}>{formatModifier(raceBonus.weaponSwords)}</td></tr>
                        <tr><td>Maces</td><td className={getModClass(raceBonus.weaponMaces)}>{formatModifier(raceBonus.weaponMaces)}</td></tr>
                        <tr><td>Staves</td><td className={getModClass(raceBonus.weaponStaves)}>{formatModifier(raceBonus.weaponStaves)}</td></tr>
                        <tr><td>Daggers / Fist</td><td className={getModClass(raceBonus.weaponDaggers)}>{formatModifier(raceBonus.weaponDaggers)}</td></tr>
                        <tr><td>Chain Weapons</td><td className={getModClass(raceBonus.weaponChain)}>{formatModifier(raceBonus.weaponChain)}</td></tr>
                        <tr><td>Shields</td><td className={getModClass(raceBonus.weaponShields)}>{formatModifier(raceBonus.weaponShields)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* Aging Stages Table - only shown on race articles */}
            {ageStages.length > 0 && (
              <section className={styles.raceBonusSection}>
                <h2 className={styles.raceBonusTitle}>Aging Stage Modifiers</h2>
                <p className={styles.raceBonusNote}>
                  These are the live stat multipliers applied as a mercenary gets older.
                </p>
                <div className={styles.ageStagesTableWrap}>
                  <table className={styles.ageStagesTable}>
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th>From (Age)</th>
                        <th>Health</th>
                        <th>Strength</th>
                        <th>Endurance</th>
                        <th>Initiative</th>
                        <th>Dodge</th>
                        <th>Leadership</th>
                        <th>Learning</th>
                        <th>Luck</th>
                        <th>Discipline</th>
                        <th>Provocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ageStages.map((s) => (
                        <tr key={s.stage}>
                          <td><strong>{s.stage}</strong></td>
                          <td>{s.thresholdDays}</td>
                          <td className={getModClass(s.health)}>{formatModifier(s.health)}</td>
                          <td className={getModClass(s.strength)}>{formatModifier(s.strength)}</td>
                          <td className={getModClass(s.endurance)}>{formatModifier(s.endurance)}</td>
                          <td className={getModClass(s.initiative)}>{formatModifier(s.initiative)}</td>
                          <td className={getModClass(s.dodge)}>{formatModifier(s.dodge)}</td>
                          <td className={getModClass(s.leadership)}>{formatModifier(s.leadership)}</td>
                          <td className={getModClass(s.learningCapacity)}>{formatModifier(s.learningCapacity)}</td>
                          <td className={getModClass(s.luck)}>{formatModifier(s.luck)}</td>
                          <td className={getModClass(s.discipline)}>{formatModifier(s.discipline)}</td>
                          <td className={getModClass(s.provocation)}>{formatModifier(s.provocation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
        {!loadingArticle && !error && !currentArticle && !loadingList && (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faBook} className={styles.emptyIcon} />
            <p>Select a topic from the sidebar to get started.</p>
          </div>
        )}
      </main>
    </div>
  );
};

function getModClass(value: number): string {
  if (value > 1) return styles.positive;
  if (value < 1) return styles.negative;
  return styles.neutral;
}

export default WikiPage;
